import { useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  ChevronRight,
  Package,
  PackageCheck,
  Thermometer,
  Truck,
  User,
  UserCog,
} from "lucide-react";
import {
  fetchAdminDelivered,
  fetchAdminRoutes,
  fetchDriverOptions,
  type AdminDeliveryRoute,
  type DriverOption,
} from "@/lib/adminDeliveryApi";
import { type DeliveredOrder } from "@/lib/driverApi";
import { getApiErrorMessage } from "@/lib/api";
import { formatDeliveredAt, formatPtDate, todayBusinessDate } from "@/lib/deliveryDate";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateNavigator } from "@/components/driver/DateNavigator";
import { EmptyState, ErrorBanner, InfoCell, Loading } from "@/components/driver/DeliveryFeedback";
import { DeliveryDetailDialog } from "@/components/admin/DeliveryDetailDialog";

const ALL_DRIVERS = "ALL";

type Tab = "rota" | "entregues";

export default function AdminDeliveries() {
  const today = useMemo(() => todayBusinessDate(), []);
  const [date, setDate] = useState<string>(today);
  const [driverId, setDriverId] = useState<string>(ALL_DRIVERS);
  const [tab, setTab] = useState<Tab>("rota");

  const [drivers, setDrivers] = useState<DriverOption[]>([]);
  const [routes, setRoutes] = useState<AdminDeliveryRoute[] | null>(null);
  const [routesError, setRoutesError] = useState<string | null>(null);
  const [delivered, setDelivered] = useState<DeliveredOrder[] | null>(null);
  const [deliveredError, setDeliveredError] = useState<string | null>(null);
  const [selectedRoute, setSelectedRoute] = useState<AdminDeliveryRoute | null>(null);
  const [selectedDelivered, setSelectedDelivered] = useState<DeliveredOrder | null>(null);

  const driverFilter = driverId === ALL_DRIVERS ? undefined : driverId;

  useEffect(() => {
    fetchDriverOptions().then(setDrivers).catch(() => setDrivers([]));
  }, []);

  useEffect(() => {
    let alive = true;
    setRoutes(null);
    setRoutesError(null);
    fetchAdminRoutes(date, driverFilter)
      .then((list) => alive && setRoutes(list))
      .catch((err) => alive && setRoutesError(getApiErrorMessage(err)));
    return () => {
      alive = false;
    };
  }, [date, driverFilter]);

  useEffect(() => {
    let alive = true;
    setDelivered(null);
    setDeliveredError(null);
    fetchAdminDelivered(date, driverFilter)
      .then((list) => alive && setDelivered(list))
      .catch((err) => alive && setDeliveredError(getApiErrorMessage(err)));
    return () => {
      alive = false;
    };
  }, [date, driverFilter]);

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 p-4 pb-6">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Entregas</h1>
        <Badge variant="outline" className="gap-1.5">
          <UserCog className="h-3 w-3" /> Visão de admin
        </Badge>
      </header>

      <div className="sticky top-2 z-10 space-y-2 rounded-xl border bg-card/95 p-1.5 shadow-sm backdrop-blur">
        <DateNavigator value={date} today={today} onChange={setDate} />
        <Select value={driverId} onValueChange={setDriverId}>
          <SelectTrigger className="h-10">
            <SelectValue placeholder="Filtrar por motorista" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_DRIVERS}>Todos os motoristas</SelectItem>
            {drivers.map((driver) => (
              <SelectItem key={driver.id} value={driver.id}>
                {driver.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value as Tab)}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="rota" className="gap-2">
            <Truck className="h-4 w-4" />
            Em rota
            {routes && routes.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                {routes.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="entregues" className="gap-2">
            <PackageCheck className="h-4 w-4" />
            Entregues
            {delivered && delivered.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                {delivered.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rota" className="space-y-3 pt-4">
          {routesError ? (
            <ErrorBanner message={routesError} />
          ) : !routes ? (
            <Loading label="Carregando rotas..." />
          ) : routes.length === 0 ? (
            <EmptyState icon={Truck} message="Nenhuma rota nessa data." />
          ) : (
            routes.map((route) => (
              <AdminRouteCard
                key={route.orderId}
                route={route}
                onClick={() => setSelectedRoute(route)}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="entregues" className="space-y-3 pt-4">
          {deliveredError ? (
            <ErrorBanner message={deliveredError} />
          ) : !delivered ? (
            <Loading label="Carregando entregas..." />
          ) : delivered.length === 0 ? (
            <EmptyState icon={PackageCheck} message="Nenhuma entrega registrada nessa data." />
          ) : (
            delivered.map((entry) => (
              <AdminDeliveredCard
                key={entry.orderId}
                delivered={entry}
                onClick={() => setSelectedDelivered(entry)}
              />
            ))
          )}
        </TabsContent>
      </Tabs>

      <DeliveryDetailDialog
        open={!!selectedRoute}
        onOpenChange={(open) => !open && setSelectedRoute(null)}
        orderId={selectedRoute?.orderId ?? null}
        source="route"
        routeSummary={selectedRoute}
      />

      <DeliveryDetailDialog
        open={!!selectedDelivered}
        onOpenChange={(open) => !open && setSelectedDelivered(null)}
        orderId={selectedDelivered?.orderId ?? null}
        source="delivered"
        deliveredSummary={selectedDelivered}
      />
    </div>
  );
}

function AdminRouteCard({ route, onClick }: { route: AdminDeliveryRoute; onClick: () => void }) {
  return (
    <Card
      onClick={onClick}
      className="group relative cursor-pointer overflow-hidden border-border/60 transition-all hover:border-primary/40 hover:shadow-md"
    >
      <span className="absolute inset-y-0 left-0 w-1 bg-primary/70" />
      <CardContent className="flex items-center gap-3 p-4">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
          <Package className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="truncate font-semibold leading-tight">{route.clientName}</div>
          <div className="text-xs text-muted-foreground">
            {route.deliveryWindow || formatPtDate(route.deliveryDate)}
          </div>
          {route.driverName && (
            <div className="flex items-center gap-1.5 text-xs">
              <User className="h-3 w-3 text-muted-foreground" />
              <span className="font-medium">{route.driverName}</span>
            </div>
          )}
        </div>
        <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
      </CardContent>
    </Card>
  );
}

function AdminDeliveredCard({ delivered, onClick }: { delivered: DeliveredOrder; onClick: () => void }) {
  return (
    <Card
      onClick={onClick}
      className="cursor-pointer overflow-hidden border-emerald-200/60 bg-emerald-50/40 transition-all hover:border-emerald-400/60 hover:shadow-md dark:bg-emerald-950/20"
    >
      <CardContent className="space-y-3 p-4">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-emerald-500/15 text-emerald-600">
            <CheckCircle2 className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="truncate font-semibold leading-tight">{delivered.clientName}</div>
            <div className="text-xs text-muted-foreground">
              Entregue às{" "}
              <span className="font-medium text-foreground">{formatDeliveredAt(delivered.deliveredAt)}</span>
            </div>
          </div>
          <Badge className="border-0 bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300">
            NF {delivered.nfNumber}
          </Badge>
        </div>
        <div className="grid gap-2 text-xs sm:grid-cols-3">
          <InfoCell icon={User} label="Motorista">
            {delivered.driverName}
          </InfoCell>
          <InfoCell icon={Thermometer} label="Temperatura">
            {delivered.truckTemperature} °C
          </InfoCell>
          <InfoCell icon={User} label="Conferente">
            {delivered.receiverName}
          </InfoCell>
        </div>
      </CardContent>
    </Card>
  );
}
